import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  initialized: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Remove sensitive logging in production
      if (process.env.NODE_ENV === 'development') {
        console.debug("[Auth] onAuthStateChange", { event, hasSession: !!nextSession });
      }
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
    });

    // THEN get current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Remove sensitive logging in production
      if (process.env.NODE_ENV === 'development') {
        console.debug("[Auth] getSession", { hasSession: !!session });
      }
      setSession(session);
      setUser(session?.user ?? null);
      setInitialized(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = useMemo(
    () => ({ user, session, initialized, signOut }),
    [user, session, initialized]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
